import { outbox } from './outbox';
// Training video. One persistent <video> element owned here, src set before any click, so the click/V handler can call
// play() synchronously (user activation, also inside the preview iframe). Local-first: the copy saved on this laptop
// (IndexedDB, see localVideo.js) plays first, then the HTTP Range stream; sources only change while idle, never mid-play.
// Nothing here ever auto-skips: a rejected play() or a media error shows the host a "PLAY TRAINING VIDEO" button; Esc skips.
import { localVideo } from './localVideo';

const BASE = process.env.REACT_APP_BACKEND_URL;
const MEDIA_ERR = { 1: 'MEDIA_ERR_ABORTED', 2: 'MEDIA_ERR_NETWORK', 3: 'MEDIA_ERR_DECODE', 4: 'MEDIA_ERR_SRC_NOT_SUPPORTED' };
const LS_KEY = 'spore.videoFailure';

const canPlay = (type) => {
  const v = document.createElement('video');
  return v.canPlayType ? v.canPlayType(type) : '';
};

export function createTrainingVideo(onChange, onEnded) {
  const el = document.createElement('video');
  el.playsInline = true;
  el.preload = 'auto';
  el.setAttribute('playsinline', '');
  el.setAttribute('data-testid', 'training-video');
  el.className = 'h-full w-full cursor-none object-contain';
  const st = {
    sources: [], idx: 0, source: null,
    error: null, playResult: null, needsClick: false, active: false, stallS: 0, iframe: window.self !== window.top,
  };
  let last = 0;
  const changed = (force) => {
    const now = Date.now();
    if (force || now - last > 250) {
      last = now;
      onChange();
    }
  };
  const setSrc = (url, label) => {
    st.source = label;
    el.dataset.source = label;
    el.src = url;
    el.load();
  };
  const streamAt = (i) => {
    st.idx = i;
    el.preload = st.sources[i].local ? 'auto' : 'metadata'; // idle streaming must not compete with the chunked save

    setSrc(st.sources[i].url, st.sources[i].variant);
  };
  const debug = () => {
    const b = el.buffered;
    return {
      readyState: el.readyState, networkState: el.networkState, currentTime: Math.round(el.currentTime * 10) / 10,
      duration: el.duration || 0, buffered: b.length ? Math.round(b.end(b.length - 1) * 10) / 10 : 0, paused: el.paused,
    };
  };
  const report = (kind, name, message) => {
    const rec = { kind, name, message, source: st.source, ...debug(), iframe: st.iframe, ua: navigator.userAgent, at: new Date().toISOString() };
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(rec));
    } catch (e) {
      /* storage disabled */
    }
    outbox.post('/api/client-log', rec);
  };
  const fail = (kind, name, message) => {
    st.error = `${st.source || 'none'}: ${name}${message ? ` ${message}` : ''}`;
    report(kind, name, message);
  };

  const tryPlay = () => {
    st.playResult = 'pending';
    let p;
    try {
      p = el.play();
    } catch (e) {
      p = Promise.reject(e);
    }
    if (!p || !p.then) return;
    p.then(() => {
      st.playResult = 'ok';
      st.needsClick = false;
      changed(true);
    }).catch((e) => {
      if (!st.active) return;
      st.playResult = `${e.name}: ${e.message}`;
      if (e.name === 'AbortError') return; // superseded by a new load (source fallback re-plays on its own)
      fail('play', e.name, e.message);
      st.needsClick = true;
      changed(true);
    });
  };

  el.addEventListener('error', () => {
    const err = el.error;
    const name = err ? MEDIA_ERR[err.code] || `code ${err.code}` : 'error';
    if (!st.sources.length) return;
    fail('media', name, err && err.message);
    const next = st.idx + 1;
    if (next < st.sources.length) {
      streamAt(next);
      if (st.active) tryPlay();
    } else if (st.active) st.needsClick = true;
    changed(true);
  });
  el.addEventListener('ended', () => st.active && onEnded('ended'));
  ['loadedmetadata', 'canplay', 'playing', 'waiting', 'progress', 'timeupdate', 'pause', 'stalled'].forEach((t) => el.addEventListener(t, () => changed()));
  setInterval(() => {
    if (!st.active || el.paused || el.readyState >= 3) {
      st.stallS = 0;
      return;
    }
    st.stallS += 1;
    if (st.stallS === 15) fail('stall', 'stall-timeout', 'still loading after 15 s (not skipping)');
    changed(true);
  }, 1000);

  let stream = [];
  const rebuild = () => {
    const lv = localVideo.state();
    const local = lv.url ? [{ variant: `local ${lv.local.origin === 'file' ? `file ${lv.local.name}` : lv.local.variant}`, url: lv.url, local: true }] : [];
    st.sources = [...local, ...stream];
    if (!st.active && st.sources.length && el.dataset.source !== st.sources[0].variant) streamAt(0);
    changed(true);
  };
  localVideo.subscribe(rebuild); // a finished save or a picked file takes over while idle, never mid-play
  (async () => {
    await localVideo.init();
    rebuild();
    try {
      const r0 = await fetch(`${BASE}/api/media/training/info`);
      if (!r0.ok) throw new Error(`info HTTP ${r0.status}`);
      const info = await r0.json();
      stream = (info.sources || []).filter((s) => canPlay(s.type)).map((s) => ({ variant: s.variant, url: `${BASE}${s.url}` }));
      if (!stream.length) throw new Error('no playable source (no H.264 or VP9 support)');
      rebuild();
      localVideo.autoSave();
    } catch (e) {
      if (!st.sources.length) {
        st.error = String(e.message || e); // offline is fine when a local copy exists
        st.unavailable = `TRAINING VIDEO NOT AVAILABLE ON THIS SERVER (${st.error}) — PICK THE FILE IN /LAB`;
      }
    }
    changed(true);
  })();

  return {
    el,
    // call synchronously from the click / keydown handler
    start() {
      Object.assign(st, { active: true, error: null, needsClick: false, stallS: 0 });
      if (!el.src) {
        fail('play', 'NoSource', st.unavailable || 'video sources not loaded yet');
        st.needsClick = true;
      } else {
        try {
          el.currentTime = 0;
        } catch (e) {
          /* not seekable yet: starts at 0 */
        }
        tryPlay();
      }
      changed(true);
    },
    retry() {
      if (!el.src) {
        st.needsClick = true; // nothing to play: keep the message up instead of an endless LOADING
        changed(true);
        return;
      }
      st.needsClick = false;
      if (el.error && st.sources.length) streamAt(st.idx);
      tryPlay();
      changed(true);
    },
    stop() {
      st.active = false;
      st.needsClick = false;
      el.pause();
      if (st.sources.length && st.idx !== 0) streamAt(0);
      try {
        el.currentTime = 0;
      } catch (e) {
        /* fine */
      }
      changed(true);
    },
    state: () => {
      const lv = localVideo.state();
      const p = lv.dl.total ? lv.dl.bytes / lv.dl.total : 0;
      return { ...st, sources: st.sources.map((s) => s.variant), ready: !!lv.local, progress: lv.local ? 1 : p, local: lv.local, dl: lv.dl, persisted: lv.persisted, ...debug() };
    },
    lastFailure: () => {
      try {
        return JSON.parse(localStorage.getItem(LS_KEY));
      } catch (e) {
        return null;
      }
    },
  };
}
